/* to test on localhost use [tenant].com.localhost:3000 as your domain */

import authentication from "@irati/auth"
import businessService from "@irati/identity/services/business"
import cookieHelper from "@irati/utils/cookies"
import {getDomain} from "@irati/utils/links"

export interface UserAccountForCookie {
  image: string
  name: string
  subdomain: string
  url: string
}

const COOKIE_NAME = "perkbox_accounts"

export const setUserAccountCookie = () => {
  // don't set cookie for ghost users
  if (authentication.isUserGhost()) {
    return
  }
  const {
    subdomain,
    branding: {businessLogoUrl},
    businessName
  } = authentication.unAuthedBusiness()

  const domain = getDomain(subdomain)
  const cookieDomain = domain.split(subdomain)[1].split(":")[0]
  const isLocalhost = cookieDomain === ".localhost"

  const existingCookie = getUserAccountCookie()
  const newEntry = {
    image: businessLogoUrl,
    name: businessName,
    subdomain,
    url: isLocalhost ? domain.replace(subdomain, `${subdomain}.com`) : domain
  }

  const newCookie = existingCookie.filter(({subdomain: cookieSubdomain}) => cookieSubdomain !== newEntry.subdomain)
  newCookie.unshift(newEntry)

  setCookie(newCookie.slice(0, 5), subdomain)
}

const setCookie = (userAccounts: UserAccountForCookie[], subdomain: string) => {
  const domain = getDomain(subdomain)
  const cookieDomain = domain.split(subdomain)[1].split(":")[0]
  const isLocalhost = cookieDomain === ".localhost"

  if (!userAccounts) {
    cookieHelper.removeItem(COOKIE_NAME, "/", isLocalhost ? ".com.localhost" : cookieDomain)
  }

  cookieHelper.setItem(
    COOKIE_NAME,
    JSON.stringify(userAccounts),
    Infinity,
    "/",
    isLocalhost ? ".com.localhost" : cookieDomain,
    !isLocalhost,
    isLocalhost ? "lax" : "none"
  )
}

export const getUserAccountCookie = (): UserAccountForCookie[] => {
  if (authentication.isUserGhost()) {
    return []
  }
  try {
    return JSON.parse(cookieHelper.getItem(COOKIE_NAME)) || []
  } catch (e) {
    return []
  }
}

export const removeUserAccountCookie = (subdomain: string) => {
  const existingCookie = getUserAccountCookie()

  if (existingCookie) {
    const newCookie = existingCookie.filter(({subdomain: cookieSubdomain}) => cookieSubdomain !== subdomain)
    setCookie(newCookie, subdomain)
  }
}

export const getUserAccountLatestLogosCookie = (userAccounts: UserAccountForCookie[], domain: string): Promise<UserAccountForCookie[]> => {
  return Promise.all(
    userAccounts.map(async ({subdomain}, index) => {
      const subdomainAccount = await businessService.getBusinessBySubdomain(subdomain).then((business) => {
        return {
          ...userAccounts[index],
          image: business.branding?.businessLogoUrl || userAccounts[index].image
        }
      })
      return subdomainAccount
    })
  ).then((subdomainAccounts) => {
    const existingCookie = getUserAccountCookie()
    if (existingCookie) {
      const newCookie = existingCookie.map((item: UserAccountForCookie) => {
        const newAccount = subdomainAccounts.find((account) => account.subdomain === item.subdomain)
        return {
          ...item,
          image: newAccount?.image || item.image
        }
      })
      setCookie(newCookie, domain)
    }

    return subdomainAccounts
  })
}
